import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';

interface Division {
  id: string;
  name_english: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
  translations: Record<string, string>;
}

interface Topic {
  id: string;
  division_id: string;
  name_english: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
  translations: Record<string, string>;
}

interface Subtopic {
  id: string;
  topic_id: string;
  name_english: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
  translations: Record<string, string>;
}

interface Scenario {
  scenario_code: string;
  name: string;
  name_english: string;
  scenario_type: string;
  category: string;
  description: string;
  formality_level: string;
  min_cefr: string;
  max_cefr: string;
  is_active: boolean;
  sort_order: number;
  subtopic_id: string;
  regions: string[];
  typical_conflict_level: number;
}

interface HierarchyResponse {
  hierarchy: {
    divisions: Division[];
    topics: Topic[];
    subtopics: Subtopic[];
  } | null;
  scenario_counts: Record<string, number>;
  stats: { total_scenarios: number };
}

interface ScenariosResponse {
  scenarios: Scenario[];
  total: number;
  dialog_counts: Record<string, number>;
}

export default function Topics() {
  const [data, setData] = useState<HierarchyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set());
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<string>>(new Set());

  const [selectedSubtopic, setSelectedSubtopic] = useState<Subtopic | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosTotal, setScenariosTotal] = useState(0);
  const [dialogCounts, setDialogCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    loadHierarchy();
  }, []);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      const resp = await apiClient<HierarchyResponse>('/api/admin/scenarios');
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const loadScenarios = async (subtopic: Subtopic) => {
    try {
      setScenariosLoading(true);
      setSelectedSubtopic(subtopic);
      const resp = await apiClient<ScenariosResponse>(
        `/api/admin/scenarios?subtopic_id=${encodeURIComponent(subtopic.id)}&limit=200`,
      );
      setScenarios(resp.scenarios);
      setScenariosTotal(resp.total);
      setDialogCounts(resp.dialog_counts || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setScenariosLoading(false);
    }
  };

  const toggleDivision = (id: string) => {
    setExpandedDivisions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTopic = (id: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSubtopic = (subtopic: Subtopic) => {
    if (selectedSubtopic?.id === subtopic.id) {
      setSelectedSubtopic(null);
      setScenarios([]);
    } else {
      loadScenarios(subtopic);
    }
    setExpandedSubtopics((prev) => {
      const next = new Set(prev);
      if (next.has(subtopic.id)) next.delete(subtopic.id);
      else next.add(subtopic.id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white mb-4">Topics (Scenario Catalog)</h1>
        <p className="text-zinc-500">Loading hierarchy...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white mb-4">Topics (Scenario Catalog)</h1>
        <p className="text-red-400">{error}</p>
        <button onClick={loadHierarchy} className="mt-2 px-3 py-1 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600">
          Retry
        </button>
      </div>
    );
  }

  if (!data?.hierarchy) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white mb-4">Topics (Scenario Catalog)</h1>
        <p className="text-zinc-500">No hierarchy data. Run the migration script:</p>
        <code className="block mt-2 text-sm text-teal-400 bg-zinc-800 p-3 rounded">
          npx tsx scripts/migrate-topics-hierarchy.ts --stage dev
        </code>
      </div>
    );
  }

  const { divisions, topics, subtopics } = data.hierarchy;
  const counts = data.scenario_counts;

  const getTopicsForDivision = (divId: string) =>
    topics.filter((t) => t.division_id === divId).sort((a, b) => a.sort_order - b.sort_order);

  const getSubtopicsForTopic = (topicId: string) =>
    subtopics.filter((st) => st.topic_id === topicId).sort((a, b) => a.sort_order - b.sort_order);

  const getTopicScenarioCount = (topicId: string) => {
    const subs = getSubtopicsForTopic(topicId);
    return subs.reduce((sum, st) => sum + (counts[st.id] || 0), 0);
  };

  const getDivisionScenarioCount = (divId: string) => {
    const divTopics = getTopicsForDivision(divId);
    return divTopics.reduce((sum, t) => sum + getTopicScenarioCount(t.id), 0);
  };

  const sortedDivisions = [...divisions].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl md:text-2xl font-semibold text-white">Topics (Scenario Catalog)</h1>
        <div className="text-sm text-zinc-400">
          {data.stats.total_scenarios.toLocaleString()} scenarios across {subtopics.length} subtopics
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Divisions" value={divisions.length} />
        <StatCard label="Topics" value={topics.length} />
        <StatCard label="Subtopics" value={subtopics.length} />
        <StatCard label="Scenarios" value={data.stats.total_scenarios} />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Tree panel */}
        <div className="w-full md:w-[480px] shrink-0 space-y-1 max-h-[50vh] md:max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
          {sortedDivisions.map((div) => (
            <DivisionNode
              key={div.id}
              division={div}
              topics={getTopicsForDivision(div.id)}
              subtopics={subtopics}
              counts={counts}
              expanded={expandedDivisions.has(div.id)}
              expandedTopics={expandedTopics}
              expandedSubtopics={expandedSubtopics}
              selectedSubtopicId={selectedSubtopic?.id ?? null}
              scenarioCount={getDivisionScenarioCount(div.id)}
              onToggle={() => toggleDivision(div.id)}
              onToggleTopic={toggleTopic}
              onSelectSubtopic={toggleSubtopic}
            />
          ))}
        </div>

        {/* Scenario detail panel */}
        <div className="flex-1 min-w-0">
          {selectedSubtopic ? (
            <ScenarioPanel
              subtopic={selectedSubtopic}
              scenarios={scenarios}
              total={scenariosTotal}
              loading={scenariosLoading}
              dialogCounts={dialogCounts}
            />
          ) : (
            <div className="border border-zinc-700 rounded-lg p-8 text-center text-zinc-500">
              Select a subtopic to view its scenarios
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DivisionNode({
  division,
  topics,
  subtopics,
  counts,
  expanded,
  expandedTopics,
  expandedSubtopics,
  selectedSubtopicId,
  scenarioCount,
  onToggle,
  onToggleTopic,
  onSelectSubtopic,
}: {
  division: Division;
  topics: Topic[];
  subtopics: Subtopic[];
  counts: Record<string, number>;
  expanded: boolean;
  expandedTopics: Set<string>;
  expandedSubtopics: Set<string>;
  selectedSubtopicId: string | null;
  scenarioCount: number;
  onToggle: () => void;
  onToggleTopic: (id: string) => void;
  onSelectSubtopic: (st: Subtopic) => void;
}) {
  const divColor = division.slug === 'a1-b1-learners'
    ? 'text-blue-400'
    : division.slug === 'stories'
      ? 'text-amber-400'
      : 'text-purple-400';

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-800/70 text-left group"
      >
        <span className="text-zinc-500 text-xs w-4">{expanded ? '▼' : '▶'}</span>
        <span className={`font-semibold ${divColor}`}>{division.name_english}</span>
        <span className="text-zinc-500 text-xs ml-auto">
          {topics.length} topics · {scenarioCount.toLocaleString()} scenarios
        </span>
      </button>

      {expanded && (
        <div className="ml-4 border-l border-zinc-800 pl-2 space-y-0.5">
          {topics.map((topic) => {
            const topicSubs = subtopics.filter((st) => st.topic_id === topic.id).sort((a, b) => a.sort_order - b.sort_order);
            const topicCount = topicSubs.reduce((sum, st) => sum + (counts[st.id] || 0), 0);
            const isExpanded = expandedTopics.has(topic.id);

            return (
              <div key={topic.id}>
                <button
                  onClick={() => onToggleTopic(topic.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 text-left text-sm"
                >
                  <span className="text-zinc-600 text-xs w-4">{topicSubs.length > 0 ? (isExpanded ? '▼' : '▶') : '·'}</span>
                  <span className="text-zinc-200">{topic.name_english}</span>
                  <span className="text-zinc-600 text-xs ml-auto">
                    {topicSubs.length > 0 && `${topicSubs.length} sub`}
                    {topicCount > 0 && ` · ${topicCount}`}
                  </span>
                </button>

                {isExpanded && topicSubs.length > 0 && (
                  <div className="ml-4 border-l border-zinc-800/50 pl-2 space-y-0.5">
                    {topicSubs.map((st) => {
                      const stCount = counts[st.id] || 0;
                      const isSelected = selectedSubtopicId === st.id;
                      return (
                        <button
                          key={st.id}
                          onClick={() => onSelectSubtopic(st)}
                          className={`w-full flex items-center gap-2 px-2 py-1 rounded text-left text-xs ${
                            isSelected
                              ? 'bg-teal-900/30 text-teal-300 ring-1 ring-teal-700'
                              : 'hover:bg-zinc-800/40 text-zinc-400'
                          }`}
                        >
                          <span className="truncate">{st.name_english}</span>
                          {stCount > 0 && (
                            <span className={`ml-auto shrink-0 ${isSelected ? 'text-teal-500' : 'text-zinc-600'}`}>
                              {stCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScenarioPanel({
  subtopic,
  scenarios,
  total,
  loading,
  dialogCounts,
}: {
  subtopic: Subtopic;
  scenarios: Scenario[];
  total: number;
  loading: boolean;
  dialogCounts: Record<string, number>;
}) {
  const totalDialogs = Object.values(dialogCounts).reduce((s, n) => s + n, 0);
  const scenariosWithDialogs = Object.values(dialogCounts).filter((n) => n > 0).length;

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-zinc-800/50 border-b border-zinc-700 flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium">{subtopic.name_english}</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{total} scenario{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-teal-400">{totalDialogs} dialog{totalDialogs !== 1 ? 's' : ''}</div>
          <p className="text-zinc-500 text-xs">{scenariosWithDialogs}/{total} scenarios have content</p>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-zinc-500 text-sm">Loading scenarios...</div>
      ) : scenarios.length === 0 ? (
        <div className="p-4 text-zinc-500 text-sm">No scenarios in this subtopic yet.</div>
      ) : (
        <div className="max-h-[calc(100vh-340px)] overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="sticky top-0 bg-zinc-900">
              <tr className="text-left text-zinc-500 text-xs">
                <th className="px-3 py-2 font-medium">Code</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Dialogs</th>
                <th className="px-3 py-2 font-medium">CEFR</th>
                <th className="px-3 py-2 font-medium">Formality</th>
                <th className="px-3 py-2 font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => {
                const dc = dialogCounts[s.scenario_code] || 0;
                return (
                  <tr key={s.scenario_code} className="hover:bg-zinc-800/40 border-t border-zinc-800/50">
                    <td className="px-3 py-1.5 font-mono text-xs text-zinc-400">{s.scenario_code}</td>
                    <td className="px-3 py-1.5 text-zinc-200 max-w-xs truncate" title={s.description}>
                      {s.name_english || s.name}
                    </td>
                    <td className="px-3 py-1.5">
                      {dc > 0 ? (
                        <span className="text-teal-400 text-xs font-medium">{dc}</span>
                      ) : (
                        <span className="text-zinc-600 text-xs">0</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-zinc-500 text-xs">{s.min_cefr}–{s.max_cefr}</td>
                    <td className="px-3 py-1.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        s.formality_level === 'formal' ? 'bg-purple-900/30 text-purple-300' :
                        s.formality_level === 'informal' ? 'bg-green-900/30 text-green-300' :
                        'bg-zinc-700/50 text-zinc-400'
                      }`}>
                        {s.formality_level}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={s.is_active ? 'text-teal-400' : 'text-zinc-600'}>
                        {s.is_active ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > scenarios.length && (
            <div className="px-3 py-2 text-xs text-zinc-500 border-t border-zinc-800">
              Showing {scenarios.length} of {total}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
      <div className="text-zinc-400 text-xs uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold text-white mt-0.5">{value.toLocaleString()}</div>
    </div>
  );
}
